<template>
  <div class="search-box">
    <div class="search-input-box">
      <div class="search-icon">
        <img src="@/assets/img/search_icon.png" alt="" />
      </div>
      <input
        type="text"
        class="search-input"
        :value="searchKeyword"
        :placeholder="placeholderText"
        @input="evt => (searchKeyword = evt.target.value)"
        @blur="handleSearchBlur"
        @focus="handleSearchFocus"
      />
    </div>
    <div class="search-result-box" v-if="showSearchResults">
      <div class="search-results">
        <div v-if="loading" class="loading-div">
          <div class="spinner-load"></div>
        </div>
        <div v-else>
          <div v-if="searchKeyword === '' && searchData.length === 0">Enter your keyword</div>
          <div v-if="searchData.length > 0">
            <div class="search-result" v-for="search in searchData" :key="search">
              <div class="search-head">{{ search.categoryName }}</div>
              <div
                class="search-data"
                v-for="lenses in search.filteredLenses"
                :key="lenses.name"
                :class="{
                  'search-scene-data': isScene,
                  'search-data-premium': isLensLocked(lenses.name)
                }"
                @click="handleSearchDataClick(lenses)"
              >
                <span>{{ lenses.name }}</span>
                <span class="badge-group" v-if="isLensLocked(lenses.name) || lenses.isNew">
                  <span v-if="lenses.isNew" class="new-badge">new</span>
                  <img
                    v-if="isLensLocked(lenses.name)"
                    :src="proLogo"
                    alt="PRO lens"
                    class="premium-badge"
                    @click.stop="emits('showPremiumAlert')"
                  />
                </span>
              </div>
            </div>
          </div>
          <div v-if="searchKeyword !== '' && searchData.length === 0">No results found.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  isScene: Boolean,
  typewriterWords: {
    type: Array,
    default: () => []
  }
})

const emits = defineEmits(['showPremiumAlert'])

import { useLensesStore } from '@/stores/Lenses'
import { useScenesStore } from '@/stores/Scenes'
import { useUsersStore } from '@/stores/Users'
import { useRouter } from 'vue-router'
import client from '@/api/client'
import { ampli } from '@/ampli'
import { createTypewriter } from '@/includes/helper'
import proLogo from '@/assets/img/PRO_logo.webp'
import {
  searchLensesByKeyword,
  mergeCategorizedResults,
  selectSearchResult
} from '@/includes/search'

const loading = ref(false)

const router = useRouter()

const lensStore = useLensesStore()
const sceneStore = useScenesStore()
const userStore = useUsersStore()

// Premium-lock badge only applies to the main lens comparison search, not scenes
function isLensLocked(name) {
  if (props.isScene) return false
  return userStore.isLoggedInValue && !lensStore.isPremiumUser && lensStore.isLensPremium(name)
}

const searchData = ref([])
const debounceTimer = ref(null)

const searchKeyword = ref('')
const showSearchResults = ref(false)

const placeholderText = ref('')
let currentPathObject = router.currentRoute.value
let typewriterInstance = null

const handleSearchFocus = () => {
  showSearchResults.value = true
  if (props.isScene) {
    sceneStore.searchIsActive = true
  } else {
    lensStore.searchIsActive = true
  }
}
const handleSearchBlur = () => {
  setTimeout(() => {
    searchKeyword.value = ''
    searchData.value = []

    showSearchResults.value = false
    if (props.isScene) {
      sceneStore.searchIsActive = false
    } else {
      lensStore.searchIsActive = false
    }
  }, 200)
}
const handleSearchDataClick = lens => {
  if (isLensLocked(lens.name)) {
    emits('showPremiumAlert')
  }

  const store = props.isScene ? sceneStore : lensStore
  selectSearchResult(store, lens)

  searchKeyword.value = ''
}
const removeDuplicates = data => {
  let lensDict = {}

  // Iterate through the array
  data.forEach(lens => {
    if (!lensDict[lens.name]) {
      // If lens name not in dict, add it
      lensDict[lens.name] = { name: lens.name, isNew: lens.isNew, isPremium: lens.isPremium }
    } else {
      // If lens name is already in dict, update isNew state
      if (lens.isNew) {
        lensDict[lens.name].isNew = true
      }
    }
  })

  // Convert the dictionary back to an array
  let uniqueLenses = Object.values(lensDict)

  return uniqueLenses
}
const searchLens = async () => {
  const endPoint = props.isScene
    ? `/lens/scenes/search?sceneType=${sceneStore.sceneType.toUpperCase()}&searchQuery=`
    : '/lens/search?query='
  try {
    /*
      AMPLITUDE: Track lens search
      Triggers when a lens search API is called
    */
    ampli.lensSearched({
      query: searchKeyword?.value,
      page: currentPathObject?.fullPath
    })

    const response = await client.get(
      `${process.env.VUE_APP_BASE_URL}${endPoint}${searchKeyword.value}`
    )

    if (response.data.code === 'auth/id-token-expired') {
      router.push('/login')
      return
    }

    let unfilteredArray = []

    unfilteredArray = props.isScene ? response.data : response.data.lens
    let filteredArray = []

    unfilteredArray.forEach(array => {
      const lensObject = array.lenses?.map(lens => {
        return { name: lens.name, isNew: lens.isNew, isPremium: lens.isPremium }
      })
      const filteredLenses = removeDuplicates(lensObject)

      filteredArray.push({
        categoryName: array.name,
        filteredLenses
      })
    })

    const keywordMatches = searchLensesByKeyword(
      props.isScene ? sceneStore.data : lensStore.data,
      searchKeyword.value
    )

    let merged = mergeCategorizedResults(filteredArray, keywordMatches)

    // Free lenses first, then premium - both within each category and across
    // categories, so a category holding only premium lenses sorts after one
    // with at least one free lens
    if (props.isScene) {
      // Non-premium users shouldn't see PRO lenses in scene search results at all
      if (!sceneStore.isPremiumUser) {
        merged = merged
          .map(category => ({
            ...category,
            filteredLenses: category.filteredLenses.filter(lens => !lens.isPremium)
          }))
          .filter(category => category.filteredLenses.length > 0)
      }
      searchData.value = merged
    } else {
      const sortedCategories = merged.map(category => ({
        ...category,
        filteredLenses: [...category.filteredLenses].sort((a, b) => {
          const aPremium = lensStore.isLensPremium(a.name)
          const bPremium = lensStore.isLensPremium(b.name)
          if (aPremium !== bPremium) return aPremium ? 1 : -1
          return a.name.localeCompare(b.name)
        })
      }))

      searchData.value = sortedCategories.sort((a, b) => {
        const aPremium = lensStore.isLensPremium(a.filteredLenses[0]?.name)
        const bPremium = lensStore.isLensPremium(b.filteredLenses[0]?.name)
        if (aPremium !== bPremium) return aPremium ? 1 : -1
        return a.categoryName.localeCompare(b.categoryName)
      })
    }
  } catch (err) {
    console.log(err)
  } finally {
    loading.value = false
  }
}

watch(searchKeyword, async () => {
  if (!searchKeyword.value) {
    searchData.value = []
    clearTimeout(debounceTimer.value)
    return
  }

  if (debounceTimer.value) clearTimeout(debounceTimer.value)

  loading.value = true

  debounceTimer.value = setTimeout(() => {
    searchLens()
  }, 500)
})

onMounted(() => {
  // If typewriter words are provided, create a typewriter effect
  if (props.typewriterWords.length > 0) {
    typewriterInstance = createTypewriter({
      words: props.typewriterWords,
      baseText: 'Search ',
      onUpdate: text => {
        placeholderText.value = text
      }
    })

    // Starting the typewriter
    typewriterInstance.start()
  }
})

onUnmounted(() => {
  // If typewriter instance exists, stop it
  if (typewriterInstance) {
    typewriterInstance.stop()
  }
})
</script>

<style scoped>
.search-box {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 20px 0;
  position: relative;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
  font-size: 16px;
}

.search-input-box {
  width: 100%;
  position: relative;
  max-width: 300px;
  margin: 0 20px;
}

.search-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 20px;
}

.search-icon img {
  width: 22px;
}

.search-input {
  width: 100%;
  background: rgba(51, 152, 219, 0.2);
  padding: 12px 60px 12px 30px;
  color: white;
  border: none;
  outline: none;
  border-radius: 30px;
  font-size: 16px;
}

.search-result-box {
  max-height: 400px;
  overflow-y: auto;
  max-width: 300px;
  margin-right: 0;
}

.empty-search-placehold {
  padding: 5px;
  color: rgb(207, 207, 207);
  font-size: 16px;
  letter-spacing: 0.5px;
}

.badge-group .new-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 0.85rem;
  margin-left: 0;
  margin-top: 0.2rem;
  padding: 0.35rem 0.3rem 0.25rem;
  font-size: 0.55rem;
  line-height: 1;
  box-sizing: border-box;
}

.search-data-premium {
  opacity: 0.3;
}

.badge-group {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.3rem;
}

.premium-badge {
  display: inline-block;
  height: 0.85rem;
  width: auto;
  vertical-align: middle;
  cursor: pointer;
  opacity: 0.85;
}
</style>
